import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "antd";
import {ProjectContext, useAppContext} from "../../context/AppContext";
import {useUser} from "../../context/UserContext";
import {CheckOperations, ComparisonOperations} from "./Operations";

// key -> message component. The serialized history drops the React components
// (JSON), so we resolve each operation's component by its key from the live
// operation configs — exactly how MessageTab/getOperationItemConfig do it.
const COMPONENT_BY_KEY = {}
for (const group of [...CheckOperations, ...ComparisonOperations]) {
    for (const child of group?.children || []) {
        if (child?.key && child?.ui?.Component) {
            COMPONENT_BY_KEY[child.key] = child.ui.Component
        }
    }
}

// Self-contained report styles so the run / category / operation / message
// hierarchy stays clearly separated regardless of the embedded app CSS.
const REPORT_CSS = `
.rr-report { padding: 24px; font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; background: #fff; }
.rr-report__title { font-size: 26px; font-weight: 700; margin: 0 0 24px; }
.rr-result { border: 1px solid #d0d5dd; border-radius: 8px; margin: 0 0 40px; overflow: hidden; }
.rr-result__title { background: #1f3a5f; color: #fff; font-size: 20px; font-weight: 600; margin: 0; padding: 12px 20px; }
.rr-overview { background: #f8fafc; border-bottom: 1px solid #e4e7ec; padding: 16px 20px; }
.rr-overview__heading { font-size: 12px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #667085; margin: 0 0 10px; }
.rr-overview__grid { display: grid; grid-template-columns: max-content 1fr; gap: 6px 18px; font-size: 13px; max-width: 920px; }
.rr-overview__label { color: #475467; font-weight: 600; white-space: nowrap; }
.rr-overview__value { color: #1f2937; }
.rr-body { padding: 16px 20px 4px; }
.rr-category { margin: 0 0 28px; }
.rr-category__title { font-size: 18px; font-weight: 700; color: #1f3a5f; margin: 0 0 14px; padding: 6px 0 6px 12px; border-left: 4px solid #1f3a5f; background: #eef2f7; }
.rr-operation { margin: 0 0 18px; }
.rr-operation__title { font-size: 13px; font-weight: 700; color: #344054; margin: 0 0 8px; text-transform: uppercase; letter-spacing: .03em; }
.rr-message { border: 1px solid #e4e7ec; border-left: 3px solid #98a2b3; border-radius: 6px; padding: 12px 16px; margin: 0 0 12px; background: #fff; }
`

// One bad message component must not abort the whole report render.
class MessageBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = {failed: false}
    }

    static getDerivedStateFromError() {
        return {failed: true}
    }

    render() {
        if (this.state.failed) {
            return <div style={{color: '#999', fontStyle: 'italic'}}>[unable to render this message]</div>
        }
        return this.props.children
    }
}

// Describe one side (base/compare) of a run from its resolved selection.
const sideInfo = (side) => {
    const info = side?.project?.information
    const project = info ? `${info.title || '-'}${info.folderName ? ` (${info.folderName})` : ''}` : '-'
    const plan = side?.plan ? `${side.plan.type || '-'}${side.plan?.plan?.ext ? ` (.${side.plan.plan.ext})` : ''}` : '-'
    const river = side?.river?.river ? `${side.river.river} - ${side.river.reach}` : '-'
    // HEC-RAS version comes from the plan file header; mirror OverviewPopup's
    // guard (only when the project actually has files parsed).
    const version = (side?.project?.projectFiles?.length > 0 && side?.plan?.plan?.version) || '-'
    return {project, plan, river, version}
}

// "What / whom" the result belongs to: type, project(s), plan(s), river(s) and
// the user who generated it.
const buildOverviewRows = (history, ctx, user) => {
    const sel = ctx?.resolveHistorySelection?.(history) || {}
    const isCompare = history?.type === 'Comparison'
    const base = sideInfo(sel.base)
    const rows = [['Type', history?.type || 'Run']]

    if (isCompare) {
        const cmp = sideInfo(sel.compare)
        rows.push(['Base Project', base.project], ['Base Plan', base.plan], ['Base HEC-RAS Version', base.version], ['Base River / Reach', base.river])
        rows.push(['Compare Project', cmp.project], ['Compare Plan', cmp.plan], ['Compare HEC-RAS Version', cmp.version], ['Compare River / Reach', cmp.river])
    } else {
        rows.push(['Project', base.project], ['Plan', base.plan], ['HEC-RAS Version', base.version], ['River / Reach', base.river])
    }

    const name = [user?.data?.name, user?.data?.lastname].filter(Boolean).join(' ')
    const who = name ? `${name}${user?.data?.email ? ` (${user.data.email})` : ''}` : user?.data?.email
    if (who) rows.push(['Generated by', who])

    return rows
}

// A single run: scope getSelectedHistory to this history so overview components
// (OV*/C-OV*) read the right summary, then render every operation that has data.
function HistoryReport({history, ctx, raw, user, index}) {
    // useAppContext derives getSelectedHistory (and every helper) from
    // context.project, so we scope this run by overriding selectedHistoryId on
    // the RAW context value — not by patching the derived object. This makes
    // overview components (OV1-OV4 / C-OV*) read THIS run's summary &
    // calculation_tolerances.
    const scoped = useMemo(() => ({
        project: {...raw?.project, selectedHistoryId: history?.id},
        dispatch: raw?.dispatch,
    }), [raw, history])
    const overviewRows = buildOverviewRows(history, ctx, user)

    return (
        <ProjectContext.Provider value={scoped}>
            <section className={'rr-result'}>
                <h2 className={'rr-result__title'}>Result {index + 1}</h2>
                <div className={'rr-overview'}>
                    <div className={'rr-overview__heading'}>Overview</div>
                    <div className={'rr-overview__grid'}>
                        {overviewRows.map(([label, value], i) => (
                            <React.Fragment key={i}>
                                <div className={'rr-overview__label'}>{label}</div>
                                <div className={'rr-overview__value'}>{value}</div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                <div className={'rr-body'}>
                    {(history?.operations || []).map((group, gi) => {
                        // Prefer the pristine originalData (full, unfiltered
                        // findings) over data (the current, possibly UI-filtered
                        // view). Only operations with findings — or informational/
                        // overview ones — are included so passed checks don't
                        // bloat the report.
                        const children = (group?.children || []).filter(c => {
                            if (!COMPONENT_BY_KEY[c?.key]) return false
                            const d = c?.originalData || c?.data
                            return (d?.result?.length > 0) || c?.informational
                        })
                        if (children.length === 0) return null
                        return (
                            <div className={'rr-category'} key={gi}>
                                <h3 className={'rr-category__title'}>{group?.title}</h3>
                                {children.map((child, ci) => {
                                    const Component = COMPONENT_BY_KEY[child.key]
                                    const data = child.originalData || child.data
                                    return (
                                        <div className={'rr-operation'} key={ci}>
                                            <h4 className={'rr-operation__title'}>{child?.title}</h4>
                                            <div className={'rr-message'}>
                                                <MessageBoundary>
                                                    <Component data={data} message_key={child.key} htmlReport={true}/>
                                                </MessageBoundary>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </section>
        </ProjectContext.Provider>
    )
}

function FullReport({ctx, user}) {
    const raw = useContext(ProjectContext)
    const histories = ctx?.appContext?.history || []
    return (
        <div className={'rr-report'}>
            <h1 className={'rr-report__title'}>RAS Review Report</h1>
            {histories.map((h, i) => <HistoryReport key={h?.id || i} history={h} ctx={ctx} raw={raw} user={user} index={i}/>)}
        </div>
    )
}

// Gather every same-origin stylesheet rule so the exported file is self-contained.
const collectCss = () => {
    let css = ''
    for (const sheet of Array.from(document.styleSheets)) {
        try {
            for (const rule of Array.from(sheet.cssRules || [])) {
                css += rule.cssText + '\n'
            }
        } catch (e) {
            // cross-origin stylesheet — its rules aren't readable; skip it.
        }
    }
    return css
}

const downloadHtml = (html, filename) => {
    const blob = new Blob([html], {type: 'text/html'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

// Renders the full report off-screen (inside the live provider tree so contexts
// and CSS apply), serializes the DOM, embeds the page styles and downloads a
// single self-contained .html file.
export default function HtmlReportButton({style}) {
    const ctx = useAppContext()
    const {user} = useUser()
    const [exporting, setExporting] = useState(false)
    const containerRef = useRef(null)

    const hasHistory = (ctx?.appContext?.history || []).length > 0

    useEffect(() => {
        if (!exporting) return
        // Let the off-screen report (and any child effects) finish rendering.
        const timer = setTimeout(() => {
            try {
                const inner = containerRef.current?.innerHTML || ''
                // App CSS first, then the report styles so the rr-* hierarchy wins.
                const css = collectCss() + REPORT_CSS
                const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>RAS Review Report</title>
<style>${css}</style>
</head>
<body style="background:#fff">${inner}</body>
</html>`
                const ts = new Date().toISOString().replaceAll(':', '').split('.')[0]
                downloadHtml(html, `ras_review_report_${ts}.html`)
            } catch (e) {
                console.error('HTML report export failed', e)
            } finally {
                setExporting(false)
            }
        }, 600)
        return () => clearTimeout(timer)
    }, [exporting])

    return (
        <>
            <Button
                size={'small'}
                type={'default'}
                style={style}
                disabled={!hasHistory || exporting}
                loading={exporting}
                onClick={() => setExporting(true)}
            >
                Download HTML
            </Button>
            {exporting && (
                <div
                    ref={containerRef}
                    aria-hidden={true}
                    style={{position: 'fixed', left: -100000, top: 0, width: 1100, background: '#fff', zIndex: -1, pointerEvents: 'none'}}
                >
                    <FullReport ctx={ctx} user={user}/>
                </div>
            )}
        </>
    )
}
